"use client";

import { useEffect, useMemo, useState } from "react";
import { compareStartupToNiche } from "@/lib/analytics";
import { formatMoney, formatPercent } from "@/lib/format";
import type { EstimatorBenchmark, NicheRanking, StartupDetails, StartupRecord } from "@/lib/types";
import { EmailCaptureForm } from "@/components/retention/email-capture-form";

type Props = {
  startup: StartupRecord | null;
  niche: NicheRanking;
  benchmark: EstimatorBenchmark;
  onClose: () => void;
};

type ApiResponse = {
  source?: string;
  detailStatus?: "hydrated" | "not_hydrated";
  coverageStatus?: "partial" | "tracked_complete" | "fully_reconciled";
  lastSyncedAt?: string | null;
  data?: StartupDetails;
  details?: string;
  error?: string;
};

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="drawer-field">
      <span className="drawer-field-label">{label}</span>
      <span className="drawer-field-value">{value}</span>
    </div>
  );
}

export function StartupDetailDrawer({
  startup,
  niche,
  benchmark,
  onClose
}: Props) {
  const [detail, setDetail] = useState<StartupDetails | null>(null);
  const [detailStatus, setDetailStatus] = useState<"hydrated" | "not_hydrated">("not_hydrated");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!startup?.slug) {
      setDetail(null);
      setError(null);
      setLoading(false);
      return;
    }

    const activeSlug = startup.slug;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`/api/startups/${encodeURIComponent(activeSlug)}`, {
          cache: "no-store"
        });
        const payload = (await response.json()) as ApiResponse;

        if (!response.ok || !payload.data) {
          throw new Error(payload.details ?? payload.error ?? "Unable to load startup details.");
        }

        if (!cancelled) {
          setDetail(payload.data);
          setDetailStatus(payload.detailStatus ?? "not_hydrated");
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Unable to load startup details.");
          setDetail(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [startup]);

  const comparison = useMemo(() => {
    if (!startup) return null;
    return compareStartupToNiche(startup, benchmark, niche);
  }, [benchmark, niche, startup]);

  if (!startup) return null;

  return (
    <div className="detail-drawer-backdrop" onClick={onClose}>
      <aside className="detail-drawer" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="detail-drawer-close" onClick={onClose}>
          Close
        </button>

        {loading && (
          <div className="detail-drawer-state">
            <div className="spinner" />
            <span className="loading-text">Loading startup detail...</span>
          </div>
        )}

        {error && !loading && <div className="error-card">{error}</div>}

        {detail && !loading && (
          <div className="detail-drawer-content">
            <div className="detail-drawer-header">
              <div className="startup-cell">
                {detail.icon && (
                  <img
                    src={detail.icon}
                    alt=""
                    className="startup-logo"
                    width={32}
                    height={32}
                  />
                )}
                <div>
                  <h3 className="card-title" style={{ marginBottom: 6 }}>{detail.name}</h3>
                  <p className="card-desc" style={{ marginBottom: 0 }}>
                    {detail.category} | {detail.payment_provider ?? "provider unknown"} | {detail.target_audience ?? "audience unknown"}
                  </p>
                </div>
              </div>

              <a
                href={detail.trustmrr_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn--ghost btn--sm"
              >
                Open TrustMRR
              </a>
            </div>

            {detail.description && (
              <p className="drawer-description">{detail.description}</p>
            )}

            {detailStatus === "not_hydrated" && (
              <div className="card" style={{ marginBottom: 20 }}>
                <p className="card-desc">
                  This startup is available from the mirror summary, but full TrustMRR detail has not been hydrated yet.
                </p>
              </div>
            )}

            <div className="drawer-grid">
              <Field label="MRR" value={formatMoney(detail.mrr)} />
              <Field label="Revenue (30d)" value={formatMoney(detail.revenue_30d)} />
              <Field label="Growth" value={formatPercent(detail.growth_30d)} />
              <Field label="Customers" value={detail.customers > 0 ? detail.customers.toLocaleString() : "-"} />
              <Field label="Followers" value={detail.x_follower_count ? detail.x_follower_count.toLocaleString() : "-"} />
              <Field label="Merchant of record" value={detail.is_merchant_of_record === null ? "-" : detail.is_merchant_of_record ? "Yes" : "No"} />
            </div>

            {comparison && (
              <div className="drawer-section">
                <p className="section-label">Market context</p>
                <div className="drawer-grid">
                  <Field label="MRR vs niche median" value={`${comparison.mrrVsMedian.toFixed(2)}x`} />
                  <Field label="Growth vs niche avg" value={formatPercent(comparison.growthVsAverage)} />
                  <Field label="Customers vs typical" value={comparison.customersVsTypical !== null ? `${comparison.customersVsTypical.toFixed(2)}x` : "-"} />
                  <Field label="Representation" value={comparison.representation} />
                </div>
                <p className="drawer-description">{comparison.summary}</p>
              </div>
            )}

            {detail.tech_stack.length > 0 && (
              <div className="drawer-section">
                <p className="section-label">Tech stack</p>
                <div className="pill-row">
                  {detail.tech_stack.map((tech) => (
                    <span key={`${tech.category}-${tech.slug}`} className="detail-pill">
                      {tech.slug}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {detail.cofounders.length > 0 && (
              <div className="drawer-section">
                <p className="section-label">Cofounders</p>
                <div className="pill-row">
                  {detail.cofounders.map((cofounder) => (
                    <span key={cofounder.x_handle} className="detail-pill">
                      {cofounder.x_name ?? cofounder.x_handle}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="drawer-section">
              <p className="section-label">Watch this startup</p>
              <EmailCaptureForm
                endpoint="/api/watches/startup"
                payload={{ startupSlug: detail.slug }}
                buttonLabel="Watch startup"
                successMessage="This startup is now on your watchlist."
                compact
              />
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
